import { Box, Stack, Typography } from '@mui/material';
import { Image } from '@realadvisor/image';
import { ImageIcon } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';

import { ListingAccordion } from '@modules/_shared/components/ListingAccordion';
import { TimeAgo } from '@modules/_shared/components/TimeAgo';
import { formatAddress } from '@modules/_shared/utils/formatting';
import { getListingPrices } from '@modules/_shared/utils/propertyTemplateFunctions';
import type { LeadDetailsDrawer } from '@modules/lead-details/type';
import { useLocale } from '@shared/hooks/locale';

type LeadDetailsLotCardProps = {
  lead: LeadDetailsDrawer;
};

export const LeadDetailsLotCard = ({ lead }: LeadDetailsLotCardProps) => {
  const { t, locale } = useLocale();

  const firstLot = lead.lots?.[0];

  if (!firstLot) {
    return null;
  }

  const address = formatAddress(firstLot.property ?? {}, ', ', true, {
    useDummyAddress: firstLot.use_dummy_address,
    dummyData: firstLot,
  });

  const prices = getListingPrices(firstLot, t, locale);
  const price = prices.primary ?? t('On request');

  const imageUrl =
    firstLot.property?.property_images?.[0]?.image?.url ?? undefined;

  return (
    <ListingAccordion title={t('Listing')}>
      <Box
        component={RouterLink}
        to={`/listings/${firstLot.id}`}
        sx={{
          display: 'flex',
          alignItems: 'center',
          borderRadius: { xs: 0, sm: 2 },
          border: '1px solid',
          borderColor: 'divider',
          borderLeftWidth: { xs: 0, sm: 1 },
          textDecoration: 'none',
          color: 'inherit',
          transition: 'all 0.15s ease',
          '&:hover': {
            borderColor: 'grey.300',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          },
        }}
      >
        <Box
          sx={{
            width: 93,
            height: 94,
            borderTopLeftRadius: { xs: 0, sm: 8 },
            borderBottomLeftRadius: { xs: 0, sm: 8 },
            overflow: 'hidden',
            flexShrink: 0,
            bgcolor: imageUrl ? undefined : 'grey.200',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'text.disabled',
          }}
        >
          {imageUrl ? (
            <Image
              src={imageUrl}
              options={{ w: 93, h: 94, f: 'jpg', c: 'fill' }}
              objectFit="cover"
            />
          ) : (
            <ImageIcon size={28} />
          )}
        </Box>

        <Stack
          sx={{
            p: 1.5,
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Typography variant="body2">{address}</Typography>
            {firstLot.updated_at ? (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  whiteSpace: 'nowrap',
                }}
              >
                <TimeAgo dateString={firstLot.updated_at} addSuffix />
              </Typography>
            ) : null}
          </Box>
          <Typography sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
            {price}
          </Typography>
        </Stack>
      </Box>
    </ListingAccordion>
  );
};
